import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import { Link as RouterLink, useLocation } from 'react-router-dom';
import Logo from '../logo.png';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  /* ================= SCROLL EFFECT ================= */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Blog', path: '/blog' },
    { label: 'Content', path: '/Content' },
    { label: 'Tutorials', path: '/tutorials' },
    { label: 'Resources', path: '/resources' },
    { label: 'About', path: '/about' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /* ================= MOBILE DRAWER ================= */
  const drawer = (
    <Box sx={{ width: 300, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={Logo} sx={{ height: 36 }} />
          <Typography fontWeight="bold" sx={{ color: '#000000' }}>AediaX</Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ mb: 2 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              borderRadius: 2,
              mb: 1,
              backgroundColor: location.pathname === item.path ? '#2563EB' : 'transparent',
              color: location.pathname === item.path ? 'white' : '#0F172A',
              '&:hover': {
                backgroundColor: '#2563EB',
                color: 'white',
              },
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* ================= HEADER ================= */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          transition: 'all 0.4s ease',
          background: scrolled 
            ? 'rgba(255,255,255,0.85)' 
            : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : 'none',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>

            {/* LOGO */}
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none' }}
            >
              <Box component="img" src={Logo} alt="AediaX Logo" sx={{ height: 40 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: scrolled ? '#0F172A' : '#FFFFFF',
                  textShadow: scrolled ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                AediaX
              </Typography>
            </Box>

            {/* DESKTOP MENU */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                
                {/* Navigation Items */}
                {navItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Button
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: scrolled ? '#334155' : '#FFFFFF',
                        fontWeight: active ? 600 : 500,
                        fontSize: '0.95rem',
                        position: 'relative',
                        px: 1.5,
                        textShadow: scrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                        '&::after': active
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 6,
                              left: '15%',
                              width: '70%',
                              height: 2,
                              backgroundColor: scrolled ? '#2563EB' : '#FFFFFF',
                              borderRadius: 2,
                              boxShadow: scrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                            }
                          : {},
                        '&:hover': {
                          color: scrolled ? '#2563EB' : '#E2E8F0',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            ) : (
              <IconButton 
                onClick={handleDrawerToggle} 
                sx={{ 
                  color: scrolled ? '#0F172A' : '#FFFFFF',
                  textShadow: scrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer 
        anchor="right" 
        open={mobileOpen} 
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;